import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import {
  Popover,
  MenuItem,
  Box,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Radio,
  Tooltip,
} from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import NestedOptionsPanel from "./NestedOptionsPanel";

const checkboxBase = (style = {}) => (
  <span
    style={{
      width: 16,
      height: 16,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 1,
      border: "2px solid #fff",
      backgroundColor: "transparent",
      ...style,
    }}
  />
);

const checkboxChecked = (
  <span
    style={{
      width: 16,
      height: 16,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 1,
      backgroundColor: "#fff",
      border: "2px solid #fff",
    }}
  >
    <svg
      aria-hidden="true"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#403C6F"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block" }}
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  </span>
);

const checkboxIndeterminate = (
  <span
    style={{
      width: 16,
      height: 16,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 1,
      backgroundColor: "#fff",
      border: "2px solid #fff",
    }}
  >
    <svg
      aria-hidden="true"
      width="12"
      height="2"
      viewBox="0 0 12 2"
      fill="none"
      style={{ display: "block" }}
    >
      <rect width="12" height="2" rx="1" fill="#403C6F" />
    </svg>
  </span>
);

const radioIcon = (
  <span
    style={{
      display: "block",
      width: 12,
      height: 12,
      borderRadius: "50%",
      border: "1px solid #fff",
      background: "transparent",
    }}
  />
);
const radioCheckedIcon = (
  <span
    style={{
      display: "block",
      width: 12,
      height: 12,
      borderRadius: "50%",
      border: "1px solid #fff",
      background: "#fff",
    }}
  />
);

const AllSelectItem = ({ noteTypesLength, selectedLength, onClick }) => {
  const allSelected = selectedLength === noteTypesLength;
  let allCheckboxAriaLabel = "All (none selected)";
  if (allSelected) {
    allCheckboxAriaLabel = "All (all selected)";
  } else if (selectedLength > 0) {
    allCheckboxAriaLabel = "All (partially selected)";
  }
  return (
    <MenuItem
      role="menuitem"
      onClick={onClick}
      sx={{
        width: "auto",
        height: 40,
        justifyContent: "space-between",
        opacity: 1,
        p: 0,
        pt: "8px",
        pr: "16px",
        pb: "8px",
        pl: "16px",
        borderRadius: "4px",
        color: "#fff",
        backgroundColor: allSelected ? "#403C6F" : "transparent",
        alignItems: "center",
        mb: "4px",
        "&:hover": {
          backgroundColor: allSelected ? "#403C6F" : "rgba(255,255,255,0.08)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          justifyContent: "space-between",
        }}
      >
        <ListItemText
          id="common-select-all-text"
          primary="All"
          slotProps={{
            primary: { sx: { color: "#fff", fontWeight: 600 } },
          }}
        />
        <ListItemIcon
          sx={{ minWidth: 32, justifyContent: "flex-end", display: "flex" }}
        >
          <Checkbox
            id="common-select-all-checkbox"
            checked={allSelected}
            indeterminate={
              selectedLength > 0 && selectedLength < noteTypesLength
            }
            icon={checkboxBase()}
            checkedIcon={checkboxChecked}
            indeterminateIcon={checkboxIndeterminate}
            slotProps={{
              input: {
                id: "common-select-all-checkbox-input",
                "aria-labelledby": "common-select-all-text",
                "aria-label": allCheckboxAriaLabel,
              },
            }}
          />
        </ListItemIcon>
      </Box>
    </MenuItem>
  );
};

const buildOptionAriaLabel = (type, isSelected, isDisabled) => {
  let ariaLabel = type;
  if (isDisabled) {
    ariaLabel += " (disabled)";
  } else if (isSelected) {
    ariaLabel += " (selected)";
  }
  return ariaLabel;
};

const getOptionHoverBackground = (isSelected, isDisabled) => {
  if (isSelected) return "#403C6F";
  if (isDisabled) return "transparent";
  return "rgba(255,255,255,0.08)";
};

const buildTrailingControl = ({
  multiple,
  isSelected,
  isDisabled,
  idx,
  baseLabelId,
  ariaLabel,
  hasChildren,
  onOpenChildren,
  handleTypeToggle,
  selectedTypes,
  nestedSelectedValue,
  type,
}) => {
  if (multiple) {
    return (
      <Checkbox
        checked={isSelected}
        disabled={isDisabled}
        icon={checkboxBase({ display: "inline-block" })}
        checkedIcon={checkboxChecked}
        slotProps={{
          input: {
            "aria-label": ariaLabel,
            id: `common-select-checkbox-${idx}`,
            "aria-labelledby": baseLabelId,
          },
        }}
      />
    );
  }

  if (hasChildren) {
    const hasNestedSelection =
      selectedTypes?.includes(type) && nestedSelectedValue;

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          maxWidth: 200,
          gap: 0.5,
        }}
      >
        <Tooltip
          title={hasNestedSelection ? nestedSelectedValue : "Select reason"}
        >
          <Box
            sx={{
              maxWidth: 140,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontSize: 12,
              color: hasNestedSelection ? "#fff" : "rgba(255,255,255,0.7)",
            }}
          >
            {hasNestedSelection ? nestedSelectedValue : "Select reason"}
          </Box>
        </Tooltip>
        <KeyboardArrowRightIcon
          sx={{
            color: "#fff",
            fontSize: 20,
          }}
        />
      </Box>
    );
  }

  return (
    <Radio
      checked={isSelected}
      disabled={isDisabled}
      sx={{
        p: 0,
        color: "#fff",
        border: "1px solid #fff",
        borderRadius: "50%",
        "&.Mui-checked": {
          color: "#fff",
          backgroundColor: "#403C6F",
          border: "1px solid #fff",
        },
      }}
      icon={radioIcon}
      checkedIcon={radioCheckedIcon}
      slotProps={{
        input: {
          "aria-label": ariaLabel,
          id: `common-select-radio-${idx}`,
          "aria-labelledby": baseLabelId,
        },
      }}
    />
  );
};

const OptionItem = ({
  type,
  idx,
  noteTypesLength,
  isSelected,
  isDisabled,
  multiple,
  showCustomOptions,
  handleTypeToggle,
  hasChildren,
  onOpenChildren,
  selectedTypes,
  nestedSelectedValue,
}) => {
  const baseLabelId = `common-select-option-text-${idx}`;
  const ariaLabel = buildOptionAriaLabel(type, isSelected, isDisabled);
  const hoverBackgroundColor = getOptionHoverBackground(isSelected, isDisabled);

  const trailingControl = buildTrailingControl({
    multiple,
    isSelected,
    isDisabled,
    idx,
    baseLabelId,
    ariaLabel,
    hasChildren,
    onOpenChildren,
    handleTypeToggle,
    selectedTypes,
    nestedSelectedValue,
    type,
  });
  return (
    <MenuItem
      role="menuitem"
      key={type}
      onClick={(event) => {
        if (isDisabled) return;
        if (
          document.activeElement &&
          typeof document.activeElement.blur === "function"
        ) {
          document.activeElement.blur();
        }
        if (hasChildren && typeof onOpenChildren === "function") {
          onOpenChildren(type, event.currentTarget);
        } else {
          handleTypeToggle(type);
        }
      }}
      disabled={isDisabled}
      sx={{
        width: "auto",
        minHeight: 40,
        height: "auto",
        justifyContent: "space-between",
        p: 0,
        pt: "8px",
        pr: "16px",
        pb: "8px",
        pl: "16px",
        borderRadius: "4px",
        color: "#fff",
        backgroundColor: isSelected ? "#403C6F" : "transparent",
        alignItems: "center",
        mb: idx === noteTypesLength - 1 ? 0 : "4px",
        "&:hover": {
          backgroundColor: hoverBackgroundColor,
        },
        cursor: isDisabled ? "not-allowed" : "pointer",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          justifyContent: "space-between",
        }}
      >
        <ListItemText
          primary={type}
          slotProps={{
            primary: {
              id: baseLabelId,
              component: "span",
              sx: {
                color: "#fff",
                fontWeight: isSelected ? 600 : "normal",
                opacity: isDisabled ? 0.5 : 1,
                whiteSpace: "normal",
                wordBreak: "break-word",
              },
            },
          }}
        />
        <ListItemIcon
          sx={{
            minWidth: 32,
            justifyContent: "flex-end",
            display: "flex",
            opacity: isDisabled ? 0.5 : 1,
          }}
        >
          {trailingControl}
        </ListItemIcon>
      </Box>
    </MenuItem>
  );
};

const CommonSelect = (props) => {
  const {
    id,
    open,
    anchorEl,
    onClose,
    inQuickview,
    filterButtonRef,
    noteTypes = [],
    selectedTypes = [],
    handleSelectAll,
    handleTypeToggle,
    multiple = false,
    width,
    disabledTypes = [],
    maxHeight,
    showCustomOptions = false,
    nestedOptions,
    nestedSelectedValue,
    onNestedSelect,
    nestedVertical = "bottom",
  } = props;

  const noteTypesLength = noteTypes.length;
  const [activeParent, setActiveParent] = useState(null);
  const [nestedAnchorEl, setNestedAnchorEl] = useState(null);

  useEffect(() => {
    if (!open) {
      setActiveParent(null);
      setNestedAnchorEl(null);
    }
  }, [open]);

  const isOutreachReason = id === "outreach-reason-select";
  return (
    <>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        disableRestoreFocus
        disableAutoFocus
        disableEnforceFocus
        anchorOrigin={{
          vertical: "bottom",
          horizontal: isOutreachReason ? "left" : "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: isOutreachReason ? "left" : "right",
        }}
        container={
          inQuickview && filterButtonRef?.current
            ? filterButtonRef.current
            : undefined
        }
        slotProps={{
          paper: {
            sx: {
              backgroundColor: "#6E6D92",
              color: "#fff",
              borderRadius: "10px",
              boxShadow: "0px 4px 16px rgba(0,0,0,0.12)",
              minWidth: width || 180,
              width: width || "auto",
              mt: 0,
              fontWeight: "semibold",
              fontSize: "16px",
              maxHeight: maxHeight || 300,
              overflowY: "auto",
            },
          },
        }}
      >
        <Box sx={{ p: "8px" }} role="menu" aria-label="Selectable options">
          {multiple && (
            <AllSelectItem
              noteTypesLength={noteTypesLength}
              selectedLength={selectedTypes.length}
              onClick={handleSelectAll}
            />
          )}
          {noteTypes.length === 0 && !multiple && (
            <MenuItem
              disabled
              sx={{ color: "rgba(255,255,255,0.7)", fontSize: "13px" }}
            >
              No options available
            </MenuItem>
          )}
          {noteTypes.map((type, idx) => {
            const hasChildren =
              nestedOptions &&
              typeof nestedOptions === "object" &&
              !Array.isArray(nestedOptions) &&
              Object.hasOwn(nestedOptions, type) &&
              Array.isArray(nestedOptions[type]) &&
              nestedOptions[type].length > 0;

            return (
              <OptionItem
                key={type}
                type={type}
                idx={idx}
                noteTypesLength={noteTypesLength}
                isSelected={selectedTypes.includes(type)}
                isDisabled={
                  Array.isArray(disabledTypes) && disabledTypes.includes(type)
                }
                multiple={multiple}
                showCustomOptions={showCustomOptions}
                selectedTypes={selectedTypes}
                nestedSelectedValue={nestedSelectedValue}
                handleTypeToggle={(val) => {
                  // When selecting a non-nested option, ensure any open
                  // nested panel is closed in the same click.
                  setActiveParent(null);
                  handleTypeToggle(val);
                }}
                hasChildren={hasChildren}
                onOpenChildren={
                  hasChildren
                    ? (clickedType, anchorElement) => {
                        setActiveParent(clickedType);
                        setNestedAnchorEl(anchorElement || anchorEl);
                      }
                    : undefined
                }
              />
            );
          })}
        </Box>
      </Popover>
      {nestedOptions &&
        activeParent &&
        Array.isArray(nestedOptions[activeParent]) && (
          <NestedOptionsPanel
            open={open && Boolean(activeParent)}
            anchorEl={nestedAnchorEl || anchorEl}
            options={nestedOptions[activeParent]}
            selectedValue={nestedSelectedValue}
            vertical={nestedVertical}
            onSelect={(value) => {
              if (onNestedSelect) {
                onNestedSelect(activeParent, value);
              }
              if (onClose) {
                onClose();
              }
            }}
            onClose={() => {
              // Closing the nested panel alone should not close the main dropdown.
              setActiveParent(null);
              setNestedAnchorEl(null);
            }}
          />
        )}
    </>
  );
};

AllSelectItem.propTypes = {
  noteTypesLength: PropTypes.number.isRequired,
  selectedLength: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
};

OptionItem.propTypes = {
  type: PropTypes.string.isRequired,
  idx: PropTypes.number.isRequired,
  noteTypesLength: PropTypes.number.isRequired,
  isSelected: PropTypes.bool.isRequired,
  isDisabled: PropTypes.bool,
  multiple: PropTypes.bool,
  showCustomOptions: PropTypes.bool,
  handleTypeToggle: PropTypes.func.isRequired,
  hasChildren: PropTypes.bool,
  onOpenChildren: PropTypes.func,
  selectedTypes: PropTypes.arrayOf(PropTypes.string),
  nestedSelectedValue: PropTypes.string,
};

export default CommonSelect;

CommonSelect.propTypes = {
  id: PropTypes.string,
  open: PropTypes.bool.isRequired,
  anchorEl: PropTypes.any,
  onClose: PropTypes.func.isRequired,
  inQuickview: PropTypes.bool,
  filterButtonRef: PropTypes.shape({ current: PropTypes.any }),
  noteTypes: PropTypes.arrayOf(PropTypes.string),
  selectedTypes: PropTypes.arrayOf(PropTypes.string),
  handleSelectAll: PropTypes.func,
  handleTypeToggle: PropTypes.func.isRequired,
  multiple: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  disabledTypes: PropTypes.arrayOf(PropTypes.string),
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  showCustomOptions: PropTypes.bool,
  nestedOptions: PropTypes.object,
  nestedSelectedValue: PropTypes.string,
  onNestedSelect: PropTypes.func,
  nestedVertical: PropTypes.oneOf(["top", "bottom"]),
};
